import React, {useContext, useRef} from 'react';
import {Card} from "../../../../../components/card/card.jsx";
import {useFormContext, useWatch} from "react-hook-form";
import useFieldArrayClean from "../../../../../hooks/use-field-array-clean.js";
import {BoFormContext} from "../context/bo-form-context.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaPlus, FaTrash} from "react-icons/fa";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import BoFormIndividualSelect from "./bo-form-individual-select.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {MaskDefault} from "../../../../../utils/mask.js";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import FormField from "../../../../../components/react-hook-form/form-field.jsx";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {useLoaderData} from "react-router";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {getCities} from "../../../../../utils/location-utils.js";
import optionsUtils from "../../../../../utils/options-utils.js";
import vehicleService from "../../../../../services/web/vehicle-service.js";
import {useQuery} from "@tanstack/react-query";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import InputFile from "../../../../../components/input/input-file.jsx";
import platePatternUtils from "../../../../../utils/plate-pattern-utils.js";
import projectUtils from "../../../../../utils/project-utils.js";

const VehicleCheckbox = ({ index, removeVehicleApprehension }) => {
    const { control, setValue, getValues } = useFormContext();

    const currentValues = useWatch({
        control,
        name: [
            `vehicleList.${index}.itemId`,
            `vehicleList.${index}.apprehensionCheckbox`,
            `vehicleList.${index}.recovered`,
            `vehicleList.${index}.removed`,
        ]
    });

    const [itemId, apprehensionCheckbox, recovered, removed] = currentValues;

    const apprehensionList = getValues("apprehensionList") ?? [];
    const currentApprehension = apprehensionList.find((item) => item?.relatedVehicle?.toString() === itemId?.toString());

    const apprehended = apprehensionCheckbox ?? !!currentApprehension;

    const getVehicleApprehensionIndex = (vehicleItemId) => {
        const apprehensionList = getValues("apprehensionList") ?? [];
        return apprehensionList.findIndex((item) => item?.relatedVehicle?.toString() === vehicleItemId?.toString());
    };

    const clearVehicleApprehensionFields = (index) => {
        setValue(`vehicleList.${index}.vehicleApprehensionDestiny`, "");
        setValue(`vehicleList.${index}.vehicleApprehensionReceiver`, "");
        setValue(`vehicleList.${index}.vehicleApprehensionDescription`, "");
    };

    const upsertVehicleApprehension = (index, vehicleItemId) => {
        const apprehensionList = getValues("apprehensionList") ?? [];
        const apprehensionIndex = getVehicleApprehensionIndex(vehicleItemId);
        const destination = getValues(`vehicleList.${index}.vehicleApprehensionDestiny`) || null;
        const receiver = getValues(`vehicleList.${index}.vehicleApprehensionReceiver`) || null;
        const description = getValues(`vehicleList.${index}.vehicleApprehensionDescription`) || null;

        const payload = {
            imei: null,
            type: "Veículo",
            unit: "UNITY",
            subtype: "AUTOMOVEL",
            quantity: 1,
            receiver,
            description,
            destination,
            relatedPerson: null,
            relatedVehicle: vehicleItemId,
        };

        if (apprehensionIndex >= 0) {
            const updated = [...apprehensionList];
            updated[apprehensionIndex] = {
                ...updated[apprehensionIndex],
                ...payload,
            };
            setValue("apprehensionList", updated);
            return;
        }

        const nextItemId = apprehensionList.length ? Math.max(...apprehensionList.map((item) => item?.itemId ?? 0)) + 1 : 1;
        setValue("apprehensionList", [...apprehensionList, {itemId: nextItemId, attachs: [], ...payload}]);
    };

    const updateVehicleApprehensionField = (index, vehicleItemId, fieldName, fieldValue) => {
        setValue(`vehicleList.${index}.${fieldName}`, fieldValue);
        upsertVehicleApprehension(index, vehicleItemId);
    };

    return (
        <>
            <FormField
                name={`vehicleList.${index}.apprehensionCheckbox`}
                customRender={(field) => (
                    <InputCheckbox
                        id={`vehicle-apprehension-${index}`}
                        checked={apprehended}
                        label={"Apreendido"}
                        className={"col-span-2"}
                        disabled={recovered || removed}
                        onChange={({target: {checked}}) => {
                            field.onChange(checked);

                            if (checked) {
                                setValue(`vehicleList.${index}.recovered`, false);
                                setValue(`vehicleList.${index}.removed`, false);
                                upsertVehicleApprehension(index, itemId);
                                return;
                            }

                            clearVehicleApprehensionFields(index);
                            removeVehicleApprehension(itemId);
                        }}
                    />
                )}
            />

            <FormField
                name={`vehicleList.${index}.recovered`}
                customRender={(field) => (
                    <InputCheckbox
                        id={`recovered-${index}`}
                        checked={recovered}
                        label={"Recuperado"}
                        className={"col-span-2"}
                        disabled={apprehended || removed}
                        onChange={({target: {checked}}) => {
                            field.onChange(checked);

                            if (checked) {
                                setValue(`vehicleList.${index}.apprehensionCheckbox`, false);
                                setValue(`vehicleList.${index}.removed`, false);
                            }
                        }}
                    />
                )}
            />

            <FormField
                name={`vehicleList.${index}.removed`}
                customRender={(field) => (
                    <InputCheckbox
                        id={`removed-${index}`}
                        checked={removed}
                        label={"Removido"}
                        className={"col-span-8"}
                        disabled={apprehended || recovered}
                        onChange={({target: {checked}}) => {
                            field.onChange(checked);

                            if (checked) {
                                setValue(`vehicleList.${index}.apprehensionCheckbox`, false);
                                setValue(`vehicleList.${index}.recovered`, false);
                            }
                        }}
                    />
                )}
            />

            {
                apprehended &&
                <>
                    <FormField
                        name={`vehicleList.${index}.vehicleApprehensionDestiny`}
                        customRender={(field) => (
                            <InputForm
                                id={`vehicle-apprehension-destiny-${index}`}
                                name={field.name}
                                label={"Destino"}
                                classNameWrapper={"col-span-3"}
                                value={field.value ?? currentApprehension?.destination ?? ""}
                                onChange={(event) => {
                                    field.onChange(event);
                                    updateVehicleApprehensionField(index, itemId, "vehicleApprehensionDestiny", event.target.value);
                                }}
                            />
                        )}
                    />

                    <FormField
                        name={`vehicleList.${index}.vehicleApprehensionReceiver`}
                        customRender={(field) => (
                            <InputForm
                                id={`vehicle-apprehension-receiver-${index}`}
                                name={field.name}
                                label={"Recebedor"}
                                classNameWrapper={"col-span-3"}
                                value={field.value ?? currentApprehension?.receiver ?? ""}
                                onChange={(event) => {
                                    field.onChange(event);
                                    updateVehicleApprehensionField(index, itemId, "vehicleApprehensionReceiver", event.target.value);
                                }}
                            />
                        )}
                    />

                    <FormField
                        name={`vehicleList.${index}.vehicleApprehensionDescription`}
                        customRender={(field) => (
                            <InputForm
                                id={`vehicle-apprehension-description-${index}`}
                                name={field.name}
                                label={"Descrição"}
                                classNameWrapper={"col-span-6"}
                                value={field.value ?? currentApprehension?.description ?? ""}
                                onChange={(event) => {
                                    field.onChange(event);
                                    updateVehicleApprehensionField(index, itemId, "vehicleApprehensionDescription", event.target.value);
                                }}
                            />
                        )}
                    />
                </>
            }
        </>
    );
}

const BoFormVehicle = () => {
    const { control, getValues, setValue, watch } = useFormContext();
    const { isSync, verifySyncUrl, setIsLoading } = useContext(BoFormContext);

    const hasPermission = useHasPermission();
    const loaderData = useLoaderData();

    const loadedPlateRef = useRef({});

    const { fields, append, remove } = useFieldArrayClean({
        control,
        name: "vehicleList"
    });

    const canSearchCortex = hasPermission(PermissionEnum.CORTEX_RENAVAM) && loaderData?.hasCortex;

    const {data: detranType} = useQuery({
        queryFn: () =>  vehicleService.getVehicleType().then(({data}) =>  data.map(item => ({value: item.id, label: item.text }))),
        queryKey: ['detranType'],
    });

    const {data: color} = useQuery({
        queryFn: () =>  vehicleService.getVehicleColor().then(({data}) =>  data.map(item => ({value: item.id, label: item.text }))),
        queryKey: ['color'],
    });

    const getBrandModels = (search, callback) => {
        if (search === '') {
            callback([]);
        }

        vehicleService.getBrandModels(search)
            .then((data) => {
                callback(data.map(({ id, text }) => ({ value: id, label: text })));
            })
    }

    const populateVehicleData = (index, data) => {
        const payload = {
            chassis: data?.chassis ?? data?.chassi ?? "",
            renavam: data?.renavam ?? "",
            fabricationYear: data?.fabricationYear ?? data?.anoFabricacao ?? "",
            modelYear: data?.modelYear ?? data?.anoModelo ?? "",
            type: data?.type ?? data?.tipoVeiculo ?? "",
            color: data?.color ?? data?.cor ?? "",
            city: data?.city ?? ((data?.municipioPlaca && data?.ufEmplacamento) ? `${data.municipioPlaca} - ${data.ufEmplacamento}` : ""),
            brandModel: data?.brandModel ?? data?.marcaModelo ?? "",
        };

        Object.entries(payload).forEach(([key, value]) => {
            setValue(`vehicleList.${index}.${key}`, value || null);
        });
    };

    const loadVehicleByPlate = async (index) => {
        const itemId = getValues(`vehicleList.${index}.itemId`);
        const plate = getValues(`vehicleList.${index}.plate`);
        const plateCleaned = (plate ?? "").replaceAll("-", "").replaceAll(" ", "");

        if (!plateCleaned || loadedPlateRef.current[itemId] === plateCleaned) return;

        try {
            const response = await vehicleService.getVehicleByPlate(plateCleaned);
            const rawData = response?.data?.data;

            if (!rawData) return;

            loadedPlateRef.current[itemId] = plateCleaned;
            populateVehicleData(index, JSON.parse(rawData));
        } catch (error) {
            console.error(error);
        }
    };

    const translateServerErrorMessage = (message) => {
        switch (message) {
            case "cortex.vehiclesearch.filter.invalid": return "O campo Placa não pode ser vazio.";
            case "cortex.token.invalid": return "Usuário sem permissão.";
            case "cortex.token.expired": return "Permissão do usuário expirada.";
            case "cortex.no.result": return "Nenhum resultado encontrado.";
            case "cortex.responseerror":
            case "cortex.ioerror":
            case "cortex.error":
            default: return "Ocorreu um erro na requisição de dados do veículo.";
        }
    };

    const searchCortex = async (index) => {
        if (!canSearchCortex) return;

        const itemId = getValues(`vehicleList.${index}.itemId`);
        const plate = getValues(`vehicleList.${index}.plate`);
        const plateValue = plate ? platePatternUtils.formatPlate(plate) : "";

        if (!plateValue) {
            projectUtils.swalMessage("warning", "", "Informe a placa para realizar a pesquisa.", "Ok");
            return;
        }

        setIsLoading(true);
        try {
            const response = await vehicleService.cortexRenavam({field: "plate", value: plateValue});
            loadedPlateRef.current[itemId] = (plate ?? "").replaceAll("-", "").replaceAll(" ", "");
            populateVehicleData(index, response?.data ?? {});
        } catch (error) {
            console.error(error);
            const message = error?.response?.status !== 500
                ? translateServerErrorMessage(error?.response?.data?.message)
                : "Ocorreu um erro na requisição de dados do veículo.";

            projectUtils.swalErrorMessage("", message, "Ok");
        } finally {
            setIsLoading(false);
        }
    };

    const addFile = (index, file, target = undefined) => {
        const attachId = Date.now();
        const newFile = Object.assign(file, {attachId});

        const attachs = watch(`vehicleList.${index}.attachs`) ?? [];

        setValue(`vehicleList.${index}.attachs`, [...attachs, newFile]);

        if (target) {
            setValue(`vehicleList.${index}.${target}`, attachId);
            setValue(`vehicleList.${index}.${target}Temp`, attachId);
        }
    };

    const removeFile = (index, attachId, target = undefined) => {
        const attachs = watch(`vehicleList.${index}.attachs`) ?? [];
        setValue(`vehicleList.${index}.attachs`, attachs.filter((attach) => attach?.attachId !== attachId));

        if (target) {
            setValue(`vehicleList.${index}.${target}`, null);
            setValue(`vehicleList.${index}.${target}Temp`, null);
        }
    };

    const removeVehicleApprehension = (vehicleItemId) => {
        const apprehensionList = getValues("apprehensionList") ?? [];
        setValue(
            "apprehensionList",
            apprehensionList.filter((item) => item?.relatedVehicle?.toString() !== vehicleItemId?.toString())
        );
    };

    return (
        <Card>
            <Card.Header>
                <Card.Title>Veículos</Card.Title>
            </Card.Header>

            <Card.Body className="space-y-4">
                {
                    fields.map((item, index) => {
                        const attachs = watch(`vehicleList.${index}.attachs`) ?? [];
                        const documentPhotoId = watch(`vehicleList.${index}.documentPhotoId`);

                        const removeDocument = !!watch(`vehicleList.${index}.documentPhotoIdTemp`);
                        const file = attachs.find((attach) => attach?.attachId === documentPhotoId);
                        if (file && !file?.file) {
                            file.url = `${verifySyncUrl(file.attachId)}/${file.attachId}`;
                        }

                        return (
                            <SentryRow className="p-4 border border-[#e5e7eb] rounded-md relative" key={"vehicle-" + index}>
                                {
                                    (isSync || !!item.__isNew) &&
                                    <Button
                                        className="absolute -top-[10px] -right-[10px] !py-0 !px-0 !w-[25px] !h-[25px] z-10"
                                        target="danger"
                                        onClick={() => {
                                            removeVehicleApprehension(item.itemId);
                                            remove(index);
                                        }}
                                    >
                                        <FaTrash />
                                    </Button>
                                }

                                <BoFormIndividualSelect
                                    name={`vehicleList.${index}.relatedPerson`}
                                    classNameWrapper="col-span-3"
                                />

                                <FormField
                                    name={`vehicleList.${index}.plate`}
                                    customRender={(field) => (
                                        <div className={"col-span-2 flex gap-2"}>
                                            <InputForm
                                                id={`vehicleList.${index}.plate`}
                                                name={field.name}
                                                label={"Placa"}
                                                classNameWrapper={"w-full"}
                                                inputClass={"uppercase"}
                                                value={field.value}
                                                onChange={(event) => {
                                                    const value = event?.target?.value;
                                                    setValue(`vehicleList.${index}.plate`, value ? platePatternUtils.formatPlate(value) : null);
                                                }}
                                                onBlur={() => {
                                                    field.onBlur();
                                                    loadVehicleByPlate(index);
                                                }}
                                            />
                                            <Button
                                                target={"primary-outline"}
                                                title={"Pesquisar no Córtex"}
                                                disabled={!canSearchCortex}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => searchCortex(index)}
                                            >
                                                <img
                                                    className={"w-[20px] h-[20px]"}
                                                    src={getAssetPath(canSearchCortex ? "img/cortex-enabled.svg" : "img/cortex-disabled.svg")}
                                                    alt=""
                                                />
                                            </Button>
                                        </div>
                                    )}
                                />

                                <FormField
                                    name={`vehicleList.${index}.city`}
                                    customRender={field => (
                                        <SelectComponent
                                            instanceId={`vehicleList.${index}.city`}
                                            classNameWrapper={"col-span-3"}
                                            loadOptions={getCities}
                                            value={optionsUtils.getValue(field.value)}
                                            isAsync={true}
                                            label={"Município"}
                                            onChange={(selected) => field.onChange(selected?.value)}
                                            cacheOptions
                                            defaultOptions={[]}
                                        />
                                    )}
                                />

                                <FormField
                                    as={InputForm}
                                    name={`vehicleList.${index}.renavam`}
                                    label={"RENAVAM"}
                                    classNameWrapper={"col-span-2"}
                                    mask={MaskDefault.renavam}
                                />

                                <FormField
                                    as={InputForm}
                                    name={`vehicleList.${index}.chassis`}
                                    label={"Chassi"}
                                    classNameWrapper={"col-span-2"}
                                />

                                <FormField
                                    name={`vehicleList.${index}.brandModel`}
                                    customRender={field => (
                                        <SelectComponent
                                            instanceId={`vehicleList.${index}.brandModel`}
                                            classNameWrapper={"col-span-3"}
                                            loadOptions={getBrandModels}
                                            value={optionsUtils.getValue(field.value)}
                                            isAsync={true}
                                            label={"Marca/modelo"}
                                            onChange={(selected) => field.onChange(selected?.value)}
                                            cacheOptions
                                            defaultOptions={[]}
                                        />
                                    )}
                                />

                                <FormField
                                    name={`vehicleList.${index}.type`}
                                    customRender={field => (
                                        <SelectComponent
                                            instanceId={`vehicleList.${index}.type`}
                                            classNameWrapper={"col-span-3"}
                                            value={optionsUtils.getValue(field.value)}
                                            options={detranType}
                                            label={"Tipo (Detran)"}
                                            onChange={(selected) => field.onChange(selected?.value)}
                                        />
                                    )}
                                />

                                <FormField
                                    as={InputForm}
                                    name={`vehicleList.${index}.fabricationYear`}
                                    label={"Ano de fabricação"}
                                    type={"number"}
                                    classNameWrapper={"col-span-2"}
                                />

                                <FormField
                                    as={InputForm}
                                    name={`vehicleList.${index}.modelYear`}
                                    label={"Ano do modelo"}
                                    type={"number"}
                                    classNameWrapper={"col-span-2"}
                                />

                                <FormField
                                    name={`vehicleList.${index}.color`}
                                    customRender={field => (
                                        <SelectComponent
                                            instanceId={`vehicleList.${index}.color`}
                                            classNameWrapper={"col-span-2"}
                                            value={optionsUtils.getValue(field.value)}
                                            options={color}
                                            label={"Cor"}
                                            onChange={(selected) => field.onChange(selected?.value)}
                                        />
                                    )}
                                />

                                <FormField
                                    as={InputTextarea}
                                    name={`vehicleList.${index}.observation`}
                                    label={"Observação"}
                                    className={"col-span-12"}
                                />

                                <VehicleCheckbox
                                    index={index}
                                    removeVehicleApprehension={removeVehicleApprehension}
                                />

                                <InputFile
                                    file={file}
                                    setFile={(file) => addFile(index, file, "documentPhotoId")}
                                    removeFile={() => removeFile(index, documentPhotoId, "documentPhotoId")}
                                    title={"Documento"}
                                    titleWithFile={"Documento"}
                                    isRemove={!!removeDocument}
                                    className={"col-span-2"}
                                />

                                {
                                    attachs.filter(a => a.attachId !== documentPhotoId).map(a => {
                                        if (a && !a?.file) a.url = `${verifySyncUrl(a.attachId)}/${a.attachId}`;

                                        return (
                                            <InputFile
                                                key={a.attachId}
                                                file={a}
                                                removeFile={() => removeFile(index, a.attachId)}
                                                titleWithFile={"Foto do veículo"}
                                                isRemove={!!a?.file}
                                                className={"col-span-2"}
                                            />
                                        )
                                    })
                                }

                                <InputFile
                                    setFile={(file) => addFile(index, file)}
                                    title={"Foto do veículo"}
                                    className={"col-span-2"}
                                />
                            </SentryRow>
                        )
                    })
                }

                <Button onClick={() => append({
                    itemId: fields?.at(-1) ? (fields?.at(-1).itemId + 1) : 1,
                    __isNew: true,
                    recovered: false,
                    removed: false,
                    apprehensionCheckbox: false
                })}>
                    <FaPlus /> Adicionar veículo
                </Button>
            </Card.Body>
        </Card>
    );
};

export default BoFormVehicle;
